import { DataSheet } from '@lune-fe/dapi-models'
import { PaginationKeys, usePaginationState } from '@lune-fe/lune-components-lib'
import { IconButton, TableNew, Text, Tooltip } from '@lune-fe/lune-ui-lib'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'
import Box from '@mui/material/Box'
import moment from 'moment'
import { FC, RefObject } from 'react'
import { useNavigate } from 'react-router-dom'

import ConditionalTooltip from 'components/ConditionalTooltip'
import { formatNumberWithFallback } from 'utils'
import { ImportsContextMenu, ImportTypeChip } from 'views/CalculateEmissions/index'
import {
    dataSheetTypeConversion,
    downloadSheet,
    getDownloadType,
} from 'views/CalculateEmissions/Shared/utils'

const ImportsTableRow: FC<{
    sheet: DataSheet
    accountNamesMap: Record<string, string>
    currentPage: number
    pageHistoryRef: RefObject<(string | undefined)[]>
    queryParams: string | undefined
}> = ({ sheet, accountNamesMap, currentPage, pageHistoryRef, queryParams }) => {
    const navigate = useNavigate()
    const { savePaginationState } = usePaginationState()

    const cleanedType = sheet.type ?? 'unknown'
    const formattedType = dataSheetTypeConversion[cleanedType]
    const processing = sheet.processedAt === null

    const downloadType = getDownloadType(cleanedType)

    const isViewable = !!sheet.options?.originalFileName

    return (
        <TableNew.TableRow>
            <TableNew.TableCell
                colSpan={12}
                colSize={12}
                sx={{ pl: `0 !important`, pr: `0 !important` }}
            >
                <ConditionalTooltip
                    show={!isViewable}
                    tooltip={({ children }) => (
                        <Tooltip
                            style={{ width: '100%' }}
                            title={'More information unavailable'}
                            placement="top"
                        >
                            {children}
                        </Tooltip>
                    )}
                >
                    <TableNew.TableContainer>
                        <TableNew
                            sx={{
                                tableLayout: 'fixed',
                            }}
                            bodyCells={
                                <TableNew.TableRow
                                    hover={!processing && isViewable}
                                    onClick={() => {
                                        if (!processing && isViewable) {
                                            savePaginationState(
                                                PaginationKeys.IMPORTS,
                                                currentPage,
                                                pageHistoryRef.current ?? [undefined],
                                            )
                                            navigate(
                                                `/imports/${sheet.id}${queryParams ? `?${queryParams}` : ''}`,
                                            )
                                        }
                                    }}
                                    data-testid={sheet.id}
                                    sx={{
                                        ...(processing
                                            ? {
                                                  opacity: 0.4,
                                                  '&:hover': {
                                                      '.context-menu': {
                                                          opacity: 0,
                                                      },
                                                  },
                                                  '.context-menu': {
                                                      pointerEvents: 'none',
                                                  },
                                              }
                                            : {
                                                  '&:hover': {
                                                      '.context-menu': {
                                                          opacity: 1,
                                                      },
                                                  },
                                              }),
                                    }}
                                >
                                    <TableNew.TableCell colSize={2}>
                                        <Box>
                                            {formattedType}
                                            {sheet.options && 'templateName' in sheet.options ? (
                                                <Text
                                                    variant={'body3'}
                                                    sx={{ color: `TextTertiary`, display: 'block' }}
                                                >
                                                    {sheet.options.templateName}
                                                </Text>
                                            ) : (
                                                ''
                                            )}
                                        </Box>
                                    </TableNew.TableCell>
                                    <TableNew.TableCell colSize={2}>
                                        {sheet.options &&
                                        'originalFileName' in sheet.options &&
                                        sheet.options.originalFileName ? (
                                            <ImportTypeChip
                                                type={cleanedType}
                                                filename={sheet.options.originalFileName}
                                            />
                                        ) : (
                                            '--'
                                        )}
                                    </TableNew.TableCell>
                                    <TableNew.TableCell colSize={2}>
                                        <Box>
                                            {moment(sheet.createdAt).format('ll')}
                                            <Text
                                                variant={'body3'}
                                                sx={{ color: `TextTertiary`, display: 'block' }}
                                            >
                                                {moment(sheet.createdAt).format('HH:mm')}
                                            </Text>
                                        </Box>
                                    </TableNew.TableCell>
                                    <TableNew.TableCell colSize={2} oneLineText>
                                        {accountNamesMap[sheet.accountId]}
                                    </TableNew.TableCell>
                                    <TableNew.TableCell alignEnd colSize={1}>
                                        {formatNumberWithFallback(sheet.numCalculations, {
                                            decimals: 0,
                                            fallback: '--',
                                        })}
                                    </TableNew.TableCell>
                                    <TableNew.TableCell alignEnd colSize={1}>
                                        {formatNumberWithFallback(sheet.numErrors, {
                                            decimals: 0,
                                            fallback: '--',
                                        })}
                                    </TableNew.TableCell>
                                    <TableNew.TableCell alignEnd colSize={1} oneLineText>
                                        {formatNumberWithFallback(sheet.quantity, {
                                            decimals: 2,
                                            decimalsWhenLessThan: 6,
                                            fallback: '--',
                                        })}
                                    </TableNew.TableCell>
                                    <TableNew.TableCell
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                        }}
                                        alignEnd
                                        colSize={1}
                                        sx={{
                                            '.context-menu': {
                                                opacity: 0,
                                            },
                                            '&:hover': {
                                                '.context-menu': {
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                    >
                                        {processing ? (
                                            <Tooltip placement="left" title="Processing">
                                                <IconButton
                                                    style={'floating'}
                                                    variant={'secondary'}
                                                    size={'sm'}
                                                    leadingIcon={<HourglassEmptyOutlinedIcon />}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <IconButton
                                                style={'floating'}
                                                variant={'secondary'}
                                                size={'sm'}
                                                onClick={() =>
                                                    downloadSheet(sheet.id, downloadType)
                                                }
                                                leadingIcon={<FileDownloadOutlinedIcon />}
                                            />
                                        )}
                                        <ImportsContextMenu sheet={sheet} />
                                    </TableNew.TableCell>
                                </TableNew.TableRow>
                            }
                        />
                    </TableNew.TableContainer>
                </ConditionalTooltip>
            </TableNew.TableCell>
        </TableNew.TableRow>
    )
}

export default ImportsTableRow
